import React, { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { uploadMediaFile, resolveMediaUrl } from "@/components/FileUpload";
import { useApp } from "@/context/AppContext";
import { ChatLightbox } from "@/components/chat/ChatLightbox";

export const TICKET_MAX_PHOTOS = 3;
const fmt = (s, vars) => Object.entries(vars).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(String(v)), s);

/** URLs of the photos that finished uploading (what the API expects in `attachments`). */
export const readyPhotoUrls = (photos) => photos.filter((p) => p.url && !p.uploading).map((p) => p.url);

// Sélecteur « Ajouter des photos » (3 max, images seulement) : envoi immédiat via /upload, vignettes + retrait.
export const TicketPhotoPicker = ({ photos, onChange, disabled, testId = "ticket-photos" }) => {
  const { t, config } = useApp();
  const inputRef = useRef(null);
  const full = photos.length >= TICKET_MAX_PHOTOS;
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const images = files.filter((f) => f.type.startsWith("image/"));
    if (images.length !== files.length) toast.info(t("support.err_photo_only_images", "Seules les photos sont acceptées."));
    const room = TICKET_MAX_PHOTOS - photos.length;
    if (images.length > room) toast.info(fmt(t("support.err_photos_max", "{max} photos maximum par message."), { max: TICKET_MAX_PHOTOS }));
    const picked = images.slice(0, Math.max(0, room));
    if (!picked.length) return;
    const drafts = picked.map((f) => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, preview: URL.createObjectURL(f), uploading: true, url: "" }));
    let next = [...photos, ...drafts];
    onChange(next);
    await Promise.all(picked.map(async (f, i) => {
      try {
        const data = await uploadMediaFile(f, { context: "support", config });
        next = next.map((p) => (p.id === drafts[i].id ? { ...p, url: data.url, uploading: false } : p));
      } catch (err) {
        toast.error(err?.friendlyMessage || t("common.error", "Erreur"));
        next = next.filter((p) => p.id !== drafts[i].id);
      }
      onChange(next);
    }));
  };
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        {photos.map((p) => (
          <div key={p.id} className="relative h-16 w-16 rounded-lg overflow-hidden border" style={{ borderColor: "var(--yalola-border)" }} data-testid={`${testId}-thumb`}>
            <img src={p.preview || resolveMediaUrl(p.url)} alt="" className="h-full w-full object-cover" />
            {p.uploading && <div className="absolute inset-0 grid place-items-center bg-black/40 text-white" aria-label={t("support.photo_uploading", "Envoi de la photo…")}><Loader2 size={16} className="animate-spin" /></div>}
            {!p.uploading && !disabled && (
              <button type="button" onClick={() => onChange(photos.filter((x) => x.id !== p.id))} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full grid place-items-center bg-black/60 text-white" aria-label={t("support.remove_photo", "Retirer la photo")} data-testid={`${testId}-remove`}><X size={11} /></button>
            )}
          </div>
        ))}
        {!full && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={disabled} className="h-16 min-w-[64px] px-2 rounded-lg border border-dashed flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium hover:bg-black/5 disabled:opacity-50" style={{ borderColor: "var(--yalola-primary)", color: "var(--yalola-primary)" }} data-testid={`${testId}-add`}>
            <ImagePlus size={18} />
            <span>{t("support.add_photos", "Ajouter des photos")} · {photos.length}/{TICKET_MAX_PHOTOS}</span>
          </button>
        )}
      </div>
      <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testId}-hint`}>{fmt(t("support.photos_hint", "Jusqu'à {max} captures d'écran pour aider l'équipe à comprendre le problème."), { max: TICKET_MAX_PHOTOS })}</p>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid={`${testId}-input`} />
    </div>
  );
};

// Photos d'un message de ticket : vignettes cliquables → visionneuse plein écran (zoom, partage, téléchargement).
export const TicketPhotoGallery = ({ photos = [], testId = "ticket-msg-photos" }) => {
  const { t } = useApp();
  const [lightbox, setLightbox] = useState(null);
  if (!photos.length) return null;
  const items = photos.map((u) => ({ url: resolveMediaUrl(u), type: "image" }));
  return (
    <>
      <div className="flex flex-wrap gap-1.5 mt-2" data-testid={testId}>
        {items.map((it, i) => (
          <button key={it.url} type="button" onClick={() => setLightbox({ items, index: i })} className="h-24 w-24 rounded-lg overflow-hidden border hover:opacity-90" style={{ borderColor: "var(--yalola-border)" }} aria-label={t("support.open_photo", "Ouvrir la photo")} data-testid={`${testId}-${i}`}>
            <img src={it.url} alt="" loading="lazy" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
      <ChatLightbox lightbox={lightbox} t={t} onClose={() => setLightbox(null)} />
    </>
  );
};
